import { useEffect, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import isEmpty from 'lodash/isEmpty';

import Checkbox, { CheckboxChangeEvent } from 'epn-ui/umd/dist/components/CheckboxInput';
import Dialog from 'epn-ui/umd/dist/components/Dialog';
import Grid from 'epn-ui/umd/dist/components/Grid';
import Loader from 'epn-ui/umd/dist/components/Loader';
import notification from 'epn-ui/umd/dist/components/Notification';
import Select from 'epn-ui/umd/dist/components/Select';

import useI18n from '@i18n';

// Components
import BaseSanitizer from '@components/base/BaseSanitizer';

import api from '@api';
import apiHooks from '@apiHooks';

import {
    IGetCreativesDataFormatted,
    IGetCreativesEridById,
    IGetDomainCuttersData,
    IGetOffersDataFormatted,
    IPostLinkReductionDataFormatted,
    IResponse,
} from '@api/types';

import { isGlobalDomain } from '@config/globalDomain';
import CopyableLink from '../../../base/BaseCopyableLink';
import BaseCopyableText from '../../../base/BaseCopyableText';

const { useReduceLinks } = apiHooks;

interface IModalEridToken {
    onClose: () => void;
    creative: IGetCreativesDataFormatted;
    offer: IGetOffersDataFormatted;
}

const backErrors = {
    'aliexpress link should be not affiliative':
        'Tokens for affiliate products and store pages will be available later',
};

const ModalEridToken: React.FC<IModalEridToken> = ({ onClose, creative, offer }) => {
    const [tokenIsLoading, setTokenIsLoading] = useState<boolean>(true);
    const [token, setToken] = useState<string>();
    const [tokenError, setTokenError] = useState<{
        title: string;
        message: string;
    } | null>();
    const [advertiserName, setAdvertiserName] = useState<string>('');
    const [reductionLoaded, setReductionLoaded] = useState<boolean>(false);
    const [reductionNeed, setReductionNeed] = useState<boolean>(true);
    const [reductionList, setReductionList] = useState<
        {
            label: string;
            value: string;
        }[]
    >(null);
    const [reductionType, setReductionType] = useState(null);

    const { t } = useI18n('Creatives');
    const { t: tErrors } = useI18n('Errors');
    const { t: tTools } = useI18n('Tools');

    const validateCreativeData = data => {
        if (isEmpty(data) || isEmpty(data.attributes)) {
            throw new Error('Data is empty');
        }
        if (
            typeof data.id !== 'string' ||
            typeof data.attributes.ordClientFullName !== 'string' ||
            typeof data.attributes.ordClientInn !== 'string'
        ) {
            throw new Error('Field types are wrong');
        }
        if (
            !data.id.length ||
            !data.attributes.ordClientFullName.length ||
            !data.attributes.ordClientInn.length
        ) {
            throw new Error('Fields is empty');
        }
    };

    const handledResponseError = errors => {
        errors.forEach(responseError => {
            if (backErrors[responseError.error_description]) {
                setTokenError({
                    title: t('Token not received'),
                    message: `<li>${t(backErrors[responseError.error_description], {
                        urlDomain: isGlobalDomain ? 'epncpa.pro' : 'epn.bz',
                    })}</li>`,
                });
            } else {
                setTokenError({
                    title: t('Token not received'),
                    message: `<li>${t('An error occurred, please try again later')}: "${
                        responseError.error_description
                    }"</li>`,
                });
                Sentry.captureException(errors);
            }
        });
    };

    const unhandledResponseError = error => {
        // Проверка для отображения другого текста ошибки для оффера Али
        if (+creative?.offerId === 1) {
            setTokenError({
                title: t('You did not receive a token for one of the following reasons:'),
                message: `<li>${t(
                    'Tokens for affiliate products and store pages will be available later',
                    { urlDomain: isGlobalDomain ? 'epncpa.pro' : 'epn.bz' },
                )}</li><li>${t('An error occurred, please try again later')}</li>`,
            });
        } else {
            console.error(error);
            Sentry.captureException(error);
            notification.error({ message: t('An error occurred, please try again later') });
            setTokenError({
                title: t('Token not received'),
                message: `<li>${t('An error occurred, please try again later')}</li>`,
            });
        }
    };

    const handleCreativeError = async error => {
        if (error?.message) {
            unhandledResponseError(error.message);
        } else {
            try {
                const res = await error.response.json();
                if (!res.result) {
                    handledResponseError(res.errors);
                }
            } catch (unhandledError) {
                unhandledResponseError(unhandledError);
            }
        }
    };

    const getToken = async () => {
        try {
            const res = (await api.creative.getCreativeErid({
                urlParams: { id: creative.id },
            })) as IResponse<IGetCreativesEridById>;
            // Проверка, потому что бек может вернуть пустые поля
            validateCreativeData(res.data);
            if (res.result) {
                setToken(res.data.id);
                const INNText = isGlobalDomain ? 'TAX number/VAT number' : 'ИНН';
                const AdvertText = isGlobalDomain ? 'Advert' : 'Реклама';
                setAdvertiserName(
                    `${AdvertText}: ${res.data.attributes.ordClientFullName} ${INNText}: ${res.data.attributes.ordClientInn}`,
                );
            }
        } catch (error) {
            handleCreativeError(error);
        } finally {
            setTokenIsLoading(false);
        }
    };

    const getDefaultReductionType = reductions => {
        let findedReduction;
        if (offer.name === 'AliExpress') {
            findedReduction = reductions.find(reduction => reduction.label === 'alii.pub');
        } else {
            findedReduction = reductions.find(reduction => reduction.label === 'shp.pub');
        }
        return findedReduction?.value || reductions[0]?.value;
    };

    const getReductions = async () => {
        try {
            const res = (await api.linkReduction.getDomainCutterList(
                {},
            )) as IResponse<IGetDomainCuttersData>;
            if (res.result && res.data?.attributes) {
                const preparedReduction = res.data?.attributes.map(item => ({
                    value: item,
                    label: item,
                }));
                setReductionList(preparedReduction);
                // Получаю дефолтный сокращатель в зависимости от типа оффера
                const defaultReduction = getDefaultReductionType(preparedReduction);
                setReductionType(defaultReduction);
                setReductionLoaded(true);
            }
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
            notification.error({ message: tErrors('Something went wrong') });
        }
    };

    useEffect(() => {
        getToken();
        getReductions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {
        data: reduceLinks = [],
        error: reduceLinkError = false,
        isLoading: reduceLoading = false,
    } = useReduceLinks<IPostLinkReductionDataFormatted[]>({
        params: {
            urlContainer: [{ url: creative?.code, domainCutter: reductionType }],
        },
        condition: creative?.code?.length > 0 && reductionNeed,
    });

    useEffect(() => {
        if (reduceLinkError) {
            console.error(reduceLinkError);
            Sentry.captureException(reduceLinkError);
            notification.error({ message: tErrors('Something went wrong') });
        }
    }, [reduceLinkError, tErrors]);

    const onChangeCheckbox = (event: CheckboxChangeEvent) => {
        setReductionNeed(event.target.checked);
    };

    const onChangeReductionLink = value => {
        setReductionType(value);
    };

    const addEridToQuery = link => {
        try {
            const creativeUrl = new URL(link);
            creativeUrl.searchParams.append('erid', token);
            return creativeUrl.toString();
        } catch (error) {
            console.error(error);
            return link;
        }
    };

    const reductionLink = reductionNeed ? reduceLinks[0]?.link || '' : creative?.code;

    const modalTitle = tokenError ? tokenError.title : t('Token received');

    return (
        <Dialog open onCancel={onClose} title={modalTitle} size="sm">
            <Loader loading={tokenIsLoading}>
                {tokenError ? (
                    <div className="my-6">
                        <BaseSanitizer
                            html={tokenError.message}
                            tag="ul"
                            className="list-decimal ml-[16px] text-blueGray-600"
                            options={{ allowedTags: ['a'] }}
                        />
                    </div>
                ) : (
                    <>
                        <div className="my-6">
                            <BaseCopyableText label={t('Token')} copyableText={token} truncate>
                                {token}
                            </BaseCopyableText>
                        </div>
                        <div className="my-6">
                            <Grid gutter={[0, 24]}>
                                <Grid.Col>
                                    <Loader loading={reduceLoading}>
                                        <CopyableLink
                                            label={t('Link')}
                                            text={addEridToQuery(reductionLink)}
                                        />
                                    </Loader>
                                </Grid.Col>
                                <Grid.Col>
                                    <Loader loading={tokenIsLoading}>
                                        <CopyableLink
                                            label={t('Advertiser')}
                                            text={advertiserName}
                                            description={
                                                isGlobalDomain
                                                    ? 'This information is needed for advertising labeling.'
                                                    : tTools(
                                                          'This information is needed for advertising labeling.',
                                                      )
                                            }
                                        />
                                    </Loader>
                                </Grid.Col>
                            </Grid>
                        </div>
                        <div className="flex items-center mt-6">
                            <Checkbox onChange={onChangeCheckbox} defaultChecked>
                                {t('Shorten link via')}:
                            </Checkbox>
                            <Select
                                value={reductionType}
                                id="reducers-list"
                                options={reductionList}
                                loading={!reductionLoaded}
                                disabled={!reductionLoaded}
                                onChange={onChangeReductionLink}
                            />
                        </div>
                    </>
                )}
            </Loader>
        </Dialog>
    );
};

export default ModalEridToken;
